import React, { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  reload,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { doc, setDoc, getDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

// Mapa de temas que usan una foto real de fondo (en vez de solo colores).
// La ruta debe existir dentro de /public para servirse tal cual. Cada
// entrada nueva de tema con foto solo necesita añadirse aquí.
const THEME_BACKGROUNDS = {
  cafe: "/theme-backgrounds/escritorio-cafe.jpg",
};

// Aplica un tema al documento: el atributo data-theme (que activa los
// colores definidos en index.css) y, si ese tema tiene foto de fondo, la
// variable --theme-bg-url con la ruta absoluta real. Esto vive en JS y no
// en el propio CSS porque Webpack (Create React App) intenta resolver
// cualquier url() dentro de un .css de src/ como módulo local, y falla al
// no encontrar la imagen (que vive en public/, no en src/).
function applyTheme(theme) {
  document.documentElement.setAttribute("data-theme", theme);
  const bgPath = THEME_BACKGROUNDS[theme];
  if (bgPath) {
    document.documentElement.style.setProperty("--theme-bg-url", `url("${bgPath}")`);
  } else {
    document.documentElement.style.removeProperty("--theme-bg-url");
  }
}

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [profile, setProfile] = useState(null);
  const [workspaceConfig, setWorkspaceConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  async function register(email, password, name, perfil, lang) {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: name });
    const L = lang || "es";
    const profileData = {
      name, email, perfil, lang: L,
      plan: "free",
      createdAt: serverTimestamp(),
      // La configuración inicial (categorías, etiquetas, etc.) ya no se
      // guarda aquí: vive en el workspace personal que crea automáticamente
      // la Cloud Function "onUserCreated" en cuanto se guarda este documento.
    };
    await setDoc(doc(db, "users", cred.user.uid), profileData);
    await sendEmailVerification(cred.user);
    return cred;
  }

  async function login(email, password) {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    if (!cred.user.emailVerified) {
      await signOut(auth);
      throw { code: "auth/email-not-verified" };
    }
    return cred;
  }

  // Acceso/registro con Google en un solo paso: si el usuario ya existía,
  // simplemente entra; si es la primera vez, creamos su perfil en
  // Firestore igual que hace register() (mismo disparador para que la
  // Cloud Function onUserCreated cree su workspace personal). Google no
  // permite elegir "personal"/"negocio" durante su propio popup, así que
  // se asigna "personal" por defecto — el usuario puede ajustar sus
  // categorías libremente después desde Ajustes si lo necesita distinto.
  // El email se considera siempre verificado (Google ya lo verifica por
  // su cuenta), así que este flujo nunca pasa por la pantalla de
  // "Verifica tu email".
  async function loginWithGoogle(lang) {
    const provider = new GoogleAuthProvider();
    const cred = await signInWithPopup(auth, provider);
    const userRef = doc(db, "users", cred.user.uid);
    const snap = await getDoc(userRef);
    if (!snap.exists()) {
      await setDoc(userRef, {
        name: cred.user.displayName || "",
        email: cred.user.email || "",
        perfil: "personal",
        lang: lang || "es",
        plan: "free",
        createdAt: serverTimestamp(),
      });
    }
    return cred;
  }

  async function resendVerification() {
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
    }
  }

  async function checkVerification() {
    if (auth.currentUser) {
      await reload(auth.currentUser);
      return auth.currentUser.emailVerified;
    }
    return false;
  }

  async function logout() {
    await signOut(auth);
    setProfile(null);
  }

  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  async function updateConfig(newConfig) {
    if (!user) return;
    const workspaceId = profile?.activeWorkspaceId || profile?.personalWorkspaceId;
    if (workspaceId) {
      // Modelo nuevo: la config vive en el workspace, compartida entre miembros
      await setDoc(doc(db, "workspaces", workspaceId), { config: newConfig }, { merge: true });
      setWorkspaceConfig(newConfig);
    } else {
      // Compatibilidad con cuentas que aún no tengan workspace asignado
      await setDoc(doc(db, "users", user.uid), { config: newConfig }, { merge: true });
      setProfile(p => ({ ...p, config: newConfig }));
    }
  }

  async function updateLang(lang) {
    if (!user) return;
    await setDoc(doc(db, "users", user.uid), { lang }, { merge: true });
    setProfile(p => ({ ...p, lang }));
  }

  async function updateCurrency(currency) {
    if (!user) return;
    await setDoc(doc(db, "users", user.uid), { currency }, { merge: true });
    setProfile(p => ({ ...p, currency }));
  }

  async function updateTheme(theme) {
    if (!user) return;
    applyTheme(theme);
    await setDoc(doc(db, "users", user.uid), { theme }, { merge: true });
    setProfile(p => ({ ...p, theme }));
  }

  async function upgradePlan(plan) {
    if (!user) return;
    await setDoc(doc(db, "users", user.uid), { plan }, { merge: true });
    setProfile(p => ({ ...p, plan }));
  }

  function getEffectivePlan(prof) {
    if (!prof) return "free";
    if (prof.plan === "pro") return "pro";
    return "free";
  }

  useEffect(() => {
    let unsubProfile = null;
    let safetyTimeout = null;

    const unsubAuth = onAuthStateChanged(auth, (u) => {
      // Si había una suscripción (o temporizador) de una sesión anterior, los cerramos
      if (unsubProfile) { unsubProfile(); unsubProfile = null; }
      if (safetyTimeout) { clearTimeout(safetyTimeout); safetyTimeout = null; }

      setUser(u);
      if (u) {
        // BUGFIX (2): antes esta suscripción solo se activaba si
        // "u.emailVerified" ya era true en el momento exacto de este
        // evento. Justo después de registrarse, ese evento se dispara con
        // emailVerified todavía en false — y cuando el usuario verifica su
        // email y pulsa "Ya verifiqué mi email" (que usa reload() +
        // navigate() manual, SIN volver a disparar onAuthStateChanged),
        // este bloque nunca se reevaluaba: "profile" se quedaba en null
        // para siempre durante toda esa sesión, aunque "user" sí fuera
        // válido (por eso no expulsaba del login, pero las categorías y
        // todo lo demás dependiente de profile/workspace nunca cargaba).
        //
        // El arreglo: suscribirse al perfil en cuanto exista CUALQUIER
        // usuario, verificado o no — el control de acceso por email sigue
        // estando correctamente en PrivateRoute (que sí lee el valor en
        // vivo de user.emailVerified en cada render). Así, cuando el
        // usuario verifica su email y navega a /panel, su perfil lleva
        // rato cargado (o cargando) de fondo, sin depender de que vuelva
        // a dispararse un evento de sesión que en este flujo no se repite.
        // BUGFIX: antes había un bucle de máximo 3 intentos (1,5s en total)
        // esperando a que el documento existiera antes de suscribirse. Si
        // el setDoc() de register() tardaba más que eso en completarse
        // (cualquier latencia normal de red, sobre todo con el paso extra
        // de updateProfile() justo antes), el bucle se rendía para
        // siempre, y la app se quedaba sin perfil — y por tanto sin
        // workspace, sin categorías, todo colgado en "Cargando..." — toda
        // esa sesión del navegador, hasta recargar o volver a iniciar
        // sesión (lo que reiniciaba el proceso, esta vez con el documento
        // ya creado de antes).
        //
        // El arreglo: suscribirse con onSnapshot de inmediato, exista o no
        // el documento todavía. onSnapshot vuelve a dispararse solo en
        // cuanto el documento se cree (no hace falta sondear con un bucle
        // ni adivinar cuánto tardará la escritura) — sin límite de tiempo
        // arbitrario que pueda rendirse antes de tiempo.
        let resolved = false;
        unsubProfile = onSnapshot(
          doc(db, "users", u.uid),
          (snap) => {
            if (!snap.exists()) return; // aún no se ha creado; seguimos esperando, sin error
            const data = snap.data();
            setProfile(data);
            if (data.theme) applyTheme(data.theme);
            resolved = true;
            setLoading(false);
          },
          (err) => {
            console.error("Error en la suscripción al perfil:", err);
            setLoading(false);
          }
        );

        // Red de seguridad: si pasan 15s y el documento sigue sin existir
        // (caso muy raro — cuenta de Auth huérfana sin perfil en
        // Firestore), no dejamos a la persona mirando una pantalla en
        // blanco para siempre. La suscripción sigue activa igualmente: si
        // los datos llegan más tarde, se actualizará sola.
        safetyTimeout = setTimeout(() => {
          if (!resolved) setLoading(false);
        }, 15000);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubAuth();
      if (unsubProfile) unsubProfile();
      if (safetyTimeout) clearTimeout(safetyTimeout);
    };
  }, []);

  const effectivePlan = getEffectivePlan(profile);
  const lang = profile?.lang || "es";
  const currency = profile?.currency || "EUR";
  const theme = profile?.theme || "light";
  const activeWorkspaceId = profile?.activeWorkspaceId || profile?.personalWorkspaceId || null;

  // NUEVO: configLoading distingue "todavía esperando datos reales" de
  // "ya sabemos que no hay nada". Cubre DOS ventanas de espera distintas:
  //   a) Cuenta recién creada: el perfil ya cargó, pero activeWorkspaceId
  //      todavía no existe (lo añade la Cloud Function onUserCreated en
  //      un segundo paso, después de crear el workspace).
  //   b) Ya hay workspace asignado, pero su config aún no ha llegado del
  //      todo (workspaceConfig sigue en null, esperando el primer snapshot).
  // La primera versión de este flag solo cubría el caso (b) — un usuario
  // recién registrado nunca pasaba por "cargando", directamente caía en
  // "false" con el desplegable de categorías vacío, igual que el bug
  // original que arreglamos en Ajustes.
  const configLoading = !!profile && (!activeWorkspaceId || workspaceConfig === null);

  // Suscripción en vivo a la config del workspace activo (categorías,
  // etiquetas, cuentas, presupuestos). Se mantiene sincronizada entre todos
  // los miembros de una cuenta compartida en tiempo real.
  useEffect(() => {
    if (!activeWorkspaceId) { setWorkspaceConfig(null); return; }
    const unsub = onSnapshot(doc(db, "workspaces", activeWorkspaceId), (snap) => {
      if (snap.exists()) setWorkspaceConfig(snap.data().config || {});
    });
    return unsub;
  }, [activeWorkspaceId]);

  // profileWithConfig: mismo objeto "profile" que ya usa toda la app, pero
  // con su "config" resuelta desde el workspace activo si existe (modelo
  // nuevo), o desde el propio perfil de usuario como fallback (cuentas
  // antiguas sin migrar). Así ningún componente existente necesita cambios.
  const profileWithConfig = profile
    ? { ...profile, config: workspaceConfig ?? profile.config ?? {} }
    : null;

  return (
    <AuthContext.Provider value={{
      user, profile: profileWithConfig, loading, lang, currency, theme,
      register, login, loginWithGoogle, logout, resetPassword,
      resendVerification, checkVerification,
      updateConfig, upgradePlan, updateLang, updateCurrency, updateTheme,
      effectivePlan,
      isPro: effectivePlan === "pro",
      configLoading,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
